import React from 'react';
import { useNavigate } from 'react-router-dom';

import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { fmtDateTime, fmtQty } from '../format';
import { GOODS_RECEIPT_STATUS } from '../status';
import type { GoodsReceiptSummary } from '../types';
import { EmptyTable } from './ListShell';
import {
  codeCls,
  dateCls,
  LinesQty,
  MappedBadge,
  metaCls,
  nameCls,
} from './ui';
import { WAREHOUSE_PATHS } from '../paths';

const COLUMNS: TableDataColumn<GoodsReceiptSummary>[] = [
  {
    key: 'code',
    title: 'GR ID',
    render: (_v, g) => <span className={codeCls}>{g.code}</span>,
  },
  {
    key: 'po',
    title: 'PO number',
    render: (_v, g) => (
      <span className={codeCls}>{g.purchaseOrderCode ?? '—'}</span>
    ),
  },
  {
    key: 'client',
    title: 'Client',
    render: (_v, g) => (
      <span className={nameCls}>
        {g.client?.name ?? `Client #${g.clientID}`}
      </span>
    ),
  },
  {
    key: 'hub',
    title: 'Hub',
    render: (_v, g) => (
      <span className={nameCls}>{g.hub?.name ?? `Hub #${g.hubID}`}</span>
    ),
  },
  {
    key: 'arrivedAt',
    title: 'Date arrival',
    render: (_v, g) => (
      <span className={dateCls}>
        {fmtDateTime(g.arrivedAt ?? g.postedAt ?? g.createdAt)}
      </span>
    ),
  },
  {
    key: 'lines',
    title: 'Lines / qty',
    align: 'right',
    render: (_v, g) => (
      <LinesQty lines={g.lineCount} qty={fmtQty(g.totalQty)} />
    ),
  },
  {
    key: 'grStatus',
    title: 'Status',
    render: (_v, g) => (
      <MappedBadge map={GOODS_RECEIPT_STATUS} value={g.status} />
    ),
  },
  {
    key: 'createdBy',
    title: 'Created by',
    render: (_v, g) => <span className={metaCls}>{g.createdBy}</span>,
  },
];

export const GoodsReceiptTable: React.FC<{
  rows: GoodsReceiptSummary[];
  loading?: boolean;
  emptyTitle?: string;
}> = ({ rows, loading, emptyTitle = 'Belum ada goods receipt' }) => {
  const navigate = useNavigate();
  return (
    <TableData
      size="sm"
      loading={loading}
      data={rows}
      columns={COLUMNS}
      onRowClick={(g) => navigate(`${WAREHOUSE_PATHS.goodsReceipt}/${g.id}`)}
      emptyState={<EmptyTable title={emptyTitle} />}
    />
  );
};
